import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Plus } from 'lucide-react';
import CustomFieldsPanel from './CustomFieldsPanel';

// Only tabs backed by a real API ship here.
//
// This page used to carry five additional tabs — Document Categories, Asset Categories,
// Notification Rules, Role Permissions and Approval Workflows — rendered from hardcoded
// arrays with read-only inputs and non-wired buttons. They were never implemented (as opposed
// to broken): there is no backend for any of them, so nothing typed into them could ever be
// saved. Shipping them behind a "Soon" badge still put fake company data (retention periods,
// depreciation rates, permission matrices) in front of HR users as if it were configuration.
//
// They are removed rather than commented out; the panels are recoverable from git history and
// should come back only alongside the endpoints that make them real. Custom Fields is fully
// functional and talks to the CustomField API.
//
// SCAFFOLDING, NOT LIVE CODE: with a single entry in this array the tab strip is suppressed, so
// `activeTab`/`setActiveTab` and the `switch` in renderTabContent below are statically unreachable
// — `activeTab` can only ever be 'custom-fields'. They are kept deliberately, not by oversight: a
// tab added back here (once it has a real backend) makes the whole mechanism live again with no
// other edit. Do not "clean up" the state or the switch.
const settingsTabs = [
 { id: 'custom-fields', label: 'Custom Fields', icon: Plus },
];

export default function CoreHRSettings() {
 const { t } = useTranslation();
 // Default to the first shipped tab rather than a hardcoded id, so removing or adding a tab
 // can never leave the page rendering a dead default.
 const [activeTab, setActiveTab] = useState(settingsTabs[0].id);

 const renderTabContent = () => {
 switch (activeTab) {
 case 'custom-fields':
 return <CustomFieldsPanel />;

 default:
 return null;
 }
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 {/* Page Header */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-6 mb-6">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
 <Settings size={24} color="white" />
 </div>
 <div>
 <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('Core HR Settings')}</h1>
 <p className="text-muted-foreground mt-1">{t("Define custom employee fields for your company's profiles and document templates")}</p>
 </div>
 </div>
 </div>

 {/* Tab Navigation — suppressed while a single tab ships, since a one-item tab strip is noise. */}
 {settingsTabs.length > 1 && (
 <div className="bg-card rounded-xl shadow-sm border border-border p-4 mb-6">
 <div className="flex items-center space-x-1 overflow-x-auto">
 {settingsTabs.map((tab) => {
 const TabIcon = tab.icon;
 const isActive = activeTab === tab.id;
 return (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
 isActive
 ? 'bg-primary/10 text-primary border border-primary/20'
 : 'text-muted-foreground hover:text-foreground hover:bg-muted'
 }`}
 >
 <TabIcon size={16} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
 <span>{t(tab.label)}</span>
 </button>
 );
 })}
 </div>
 </div>
 )}

 {/* Tab Content */}
 <div className="mb-6">
 {renderTabContent()}
 </div>
 </div>
 );
}
